import { useEffect, useState } from 'react';
import { generatePath } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { callUiApi } from '../../../common/fetch.js';
import { useSearchParams } from '../../../common/hooks/useSearchParams.js';
import { savingTheDataFailedMsg, somethingWentWrongMsg, t } from '../../../common/i18n/index.js';
import {
  batchGetCallerDisplayNamesPrivateMethod,
  bulkUpsertCallerDisplayNamesPrivateMethod,
  deleteCallerDisplayNamePrivateMethod,
  getSubscriptionsPrivateMethod,
} from '../../../generated/api/uiApiMethods.js';
import { addNotification } from '../../../public/site/notificationStore.js';
import { DEFAULT_ITEMS_PER_PAGE, type DefaultListSearchParams } from '../../Table/index.js';
import { ALL_CALLER_DISPLAY_SUBSCRIPTION_TYPES } from './callerDisplayFilters.js';
import type { CallerDisplayNameBatchResult } from '../../../generated/api/callerDisplayNameBatchResult.js';
import type { OnlineApiError } from '../../../generated/api/onlineApiError.js';
import type { PutCallerDisplayNamesResponse } from '../../../generated/api/putCallerDisplayNamesResponse.js';
import type { SubscriptionSearchResponse } from '../../../generated/api/subscriptionSearchResponse.js';
import type { SubscriptionsResponse } from '../../../generated/api/subscriptionsResponse.js';
import type { CallerDisplayNameRowData } from '../CallerDisplayNameSettingRow/CallerDisplayNameSettingRowView.js';

const MAX_ITEMS_PER_PAGE = 100;

interface CallerDisplayNameApiItem {
  phoneNumber: string;
  user: string;
  owner: string;
  activeDisplayName: string;
  originalDisplayName: string;
  isOverride: boolean;
  subscriptionDisplayId?: string;
  subscriptionLink?: string;
  pbxType?: string;
  corporateNumber?: string;
  corporateActiveDisplayName?: string;
  corporateOriginalDisplayName?: string;
  corporateUser?: string;
  corporateOwner?: string;
}

interface CallerDisplayNamesApiResponse {
  customNumbers: CallerDisplayNameApiItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface BatchCallerDisplayNamesResponse {
  results: CallerDisplayNameBatchResult[];
}

const getApiErrorMessage = async (error: unknown): Promise<string | undefined> => {
  if (!(error instanceof Response)) {
    return undefined;
  }
  const errorResponse = (await error.json().catch(() => undefined)) as OnlineApiError | undefined;
  return errorResponse?.error;
};

/** Strips Finnish country code (358) or local leading zero so both formats match. */
const normalizePhoneNumber = (phone: string): string => {
  if (phone.startsWith('358')) {
    return phone.slice(3);
  }
  if (phone.startsWith('+358')) {
    return phone.slice(4);
  }
  if (phone.startsWith('0')) {
    return phone.slice(1);
  }
  return phone;
};

const buildCallerDisplayItems = (
  searchResults: SubscriptionSearchResponse[],
  batchResults: CallerDisplayNameBatchResult[]
): CallerDisplayNameApiItem[] => {
  const batchMap = new Map(batchResults.map(r => [normalizePhoneNumber(r.phoneNumber), r]));
  return searchResults
    .filter(sr => sr.result.subscriptionUserFriendlyId || sr.result.corporateNumber)
    .map(sr => {
      const sub = sr.result;
      const phoneNumber = sub.subscriptionUserFriendlyId ?? '';
      const batchResult = phoneNumber ? batchMap.get(normalizePhoneNumber(phoneNumber)) : undefined;
      const corporateResult = sub.corporateNumber ? batchMap.get(normalizePhoneNumber(sub.corporateNumber)) : undefined;
      // Use corporate result for corporate-only row
      const primaryResult = phoneNumber ? batchResult : corporateResult;
      const subscriptionId = sub.subscriptionDisplayId ?? sub.subscriptionId;

      return {
        phoneNumber,
        user: batchResult?.user ?? '',
        owner: batchResult?.owner ?? '',
        activeDisplayName: batchResult?.displayName ?? '',
        originalDisplayName: batchResult?.originalDisplayName ?? '',
        isOverride: primaryResult?.isOverride ?? false,
        subscriptionDisplayId: subscriptionId,
        subscriptionLink: `${generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId })}?mdmId=${sub.accountMasterId ?? ''}`,
        pbxType: sub.pbxType,
        corporateNumber: sub.corporateNumber,
        corporateActiveDisplayName: corporateResult?.displayName,
        corporateOriginalDisplayName: corporateResult?.originalDisplayName,
        corporateUser: corporateResult?.user,
        corporateOwner: corporateResult?.owner,
      };
    });
};

export const useCallerDisplayNameSetting = () => {
  const [editingValues, setEditingValues] = useState<
    Record<string, { user: string; owner: string; corporateUser?: string; corporateOwner?: string }>
  >({});
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [confirmResetRow, setConfirmResetRow] = useState<CallerDisplayNameRowData | undefined>();
  const [apiResponse, setApiResponse] = useState<CallerDisplayNamesApiResponse | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const searchParams = useSearchParams<DefaultListSearchParams & { subscriptionType?: string; mdmId?: string }>();
  const { search, offset, limit, sort, order, subscriptionType, mdmId } = searchParams;
  const searchQuery = search?.toLowerCase() ?? '';
  const parsedLimit = Math.min(limit ? Number(limit) : DEFAULT_ITEMS_PER_PAGE, MAX_ITEMS_PER_PAGE);
  const parsedOffset = offset ? Number(offset) : 0;
  const activeSubscriptionTypes = subscriptionType
    ? subscriptionType.split('|')
    : ALL_CALLER_DISPLAY_SUBSCRIPTION_TYPES;

  const fetchCallerDisplayNames = async () => {
    setIsLoading(true);
    try {
      const subscriptionRes = await callUiApi(
        getSubscriptionsPrivateMethod({
          subscriptionType: activeSubscriptionTypes,
          useSearchService: true,
          search,
          limit: parsedLimit,
          offset: parsedOffset,
          sort,
          order,
        }),
        { mdmId, useDefaultMdmId: false }
      );
      const { searchResults = [], total } = (await subscriptionRes.json()) as SubscriptionsResponse;

      const phoneNumbers = [
        ...new Set(
          searchResults.flatMap(sr => [sr.result.subscriptionUserFriendlyId, sr.result.corporateNumber]).filter(Boolean)
        ),
      ] as string[];

      if (phoneNumbers.length === 0) {
        setApiResponse(current => ({
          ...(current ?? { page: 1, limit: parsedLimit, totalPages: 1 }),
          customNumbers: [],
          total: total ?? 0,
        }));
        return;
      }

      const callerDisplayRes = await callUiApi(batchGetCallerDisplayNamesPrivateMethod(), {
        body: JSON.stringify({ phoneNumbers }),
      });
      const { results } = (await callerDisplayRes.json()) as BatchCallerDisplayNamesResponse;

      const merged = buildCallerDisplayItems(searchResults, results);
      setApiResponse(current => ({
        ...(current ?? { page: 1, limit: parsedLimit, totalPages: 1 }),
        customNumbers: merged,
        total: total ?? merged.length,
      }));
    } catch (error) {
      setApiResponse(undefined);
      const errorMessage = await getApiErrorMessage(error);
      addNotification(errorMessage ?? t.JDTO(somethingWentWrongMsg), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCallerDisplayNames();
  }, [search, offset, limit, sort, order, subscriptionType, mdmId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows: CallerDisplayNameRowData[] = (apiResponse?.customNumbers ?? []).map(item => ({
    id: item.phoneNumber || item.corporateNumber!,
    phoneNumber: item.phoneNumber,
    displayUser: item.user,
    displayCompany: item.owner,
    activeDisplayName: item.activeDisplayName,
    originalDisplayName: item.originalDisplayName,
    subscriptionDisplayId: item.subscriptionDisplayId ?? '',
    subscriptionLink: item.subscriptionLink ?? '',
    isOverride: item.isOverride,
    pbxType: item.pbxType as CallerDisplayNameRowData['pbxType'],
    corporateNumber: item.corporateNumber,
    corporateActiveDisplayName: item.corporateActiveDisplayName,
    corporateOriginalDisplayName: item.corporateOriginalDisplayName,
    corporateDisplayUser: item.corporateUser,
    corporateDisplayCompany: item.corporateOwner,
  }));

  const editCallerDisplayRow = (row: CallerDisplayNameRowData) => {
    setEditingValues({
      [row.id]: {
        user: row.displayUser,
        owner: row.displayCompany,
        ...(row.corporateNumber
          ? { corporateUser: row.corporateDisplayUser ?? '', corporateOwner: row.corporateDisplayCompany ?? '' }
          : {}),
      },
    });
  };

  const cancelEdit = () => {
    setEditingValues({});
    setIsBulkEdit(false);
  };

  const startTableEditing = () => {
    setEditingValues(
      Object.fromEntries(
        rows.map(row => [
          row.id,
          {
            user: row.displayUser,
            owner: row.displayCompany,
            ...(row.corporateNumber
              ? { corporateUser: row.corporateDisplayUser ?? '', corporateOwner: row.corporateDisplayCompany ?? '' }
              : {}),
          },
        ])
      )
    );
    setIsBulkEdit(true);
  };

  const submitEdit = () => {
    const numbers: Array<{ phoneNumber: string; user: string; owner: string }> = [];
    for (const [id, values] of Object.entries(editingValues)) {
      const row = rows.find(r => r.id === id);
      if (row?.phoneNumber) {
        numbers.push({ phoneNumber: row.phoneNumber, user: values.user, owner: values.owner });
      }
      if (row?.corporateNumber && (values.corporateUser !== undefined || values.corporateOwner !== undefined)) {
        numbers.push({
          phoneNumber: row.corporateNumber,
          user: values.corporateUser ?? '',
          owner: values.corporateOwner ?? '',
        });
      }
    }
    if (numbers.length === 0) {
      return;
    }
    callUiApi(bulkUpsertCallerDisplayNamesPrivateMethod(), {
      body: JSON.stringify({ numbers }),
    })
      .then(async response => {
        const result = (await response.json()) as PutCallerDisplayNamesResponse;
        fetchCallerDisplayNames();
        cancelEdit();
        if (result.failed > 0) {
          const failures = result.results
            .filter(item => !item.success)
            .map(item => `${item.phoneNumber}: ${item.error ?? t.JDTO(somethingWentWrongMsg)}`)
            .join('; ');
          addNotification(`${t.RQA1(savingTheDataFailedMsg)} ${failures}`, 'warning');
        } else {
          addNotification(t.Z9HQ('The connection display information was successfully updated.'), 'success');
        }
      })
      .catch(async error => {
        const errorMessage = await getApiErrorMessage(error);
        addNotification(errorMessage ?? t.RQA1(savingTheDataFailedMsg), 'error');
      });
  };

  const setEditingValue = (
    rowId: string,
    field: 'user' | 'owner' | 'corporateUser' | 'corporateOwner',
    value: string
  ) => {
    setEditingValues(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] ?? { user: '', owner: '' }), [field]: value },
    }));
  };

  const resetCallerDisplayRow = (row: CallerDisplayNameRowData) => {
    setConfirmResetRow(row);
  };

  const confirmResetCallerDisplayRow = async () => {
    if (!confirmResetRow) {
      return;
    }
    const phoneNumbers = new Set([confirmResetRow.id]);
    if (
      confirmResetRow.corporateNumber &&
      (confirmResetRow.corporateActiveDisplayName ||
        confirmResetRow.corporateDisplayUser ||
        confirmResetRow.corporateDisplayCompany)
    ) {
      phoneNumbers.add(confirmResetRow.corporateNumber);
    }
    const results = await Promise.allSettled(
      [...phoneNumbers].map(phoneNumber => callUiApi(deleteCallerDisplayNamePrivateMethod(phoneNumber)))
    );
    setConfirmResetRow(undefined);
    await fetchCallerDisplayNames();
    if (results.some(result => result.status === 'rejected')) {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
    }
  };

  return {
    rows,
    isLoading,
    totalItems: apiResponse?.total ?? 0,
    searchQuery,
    editingValues,
    isBulkEdit,
    confirmResetRow,
    setConfirmResetRow,
    editCallerDisplayRow,
    cancelEdit,
    startTableEditing,
    submitEdit,
    setEditingValue,
    resetCallerDisplayRow,
    confirmResetCallerDisplayRow,
  };
};
